/**
 * Structured CLI documentation — AUTO-GENERATED from src/Cli/README.md
 * Do not edit manually. Run: npm run generate-docs
 */

export interface DocStep {
  title: string;
  description?: string;
  code: string;
}

export interface DocCommand {
  name: string;
  description: string;
  usage: string;
  options?: { option: string; alias?: string; description: string; default?: string }[];
  examples?: { description: string; code: string; output?: string }[];
}

export interface DocWorkflow {
  title: string;
  description: string;
  code: string;
}

export const CLI_DOC = {
  title: 'W365A CLI',
  subtitle: 'Command-line tool for managing W365A sandbox environments.',

  installation: {
    steps: [
      {
            title: "Build",
            code: "dotnet build src/Cli/Cli.csproj"
      },
      {
            title: "Run directly",
            code: "dotnet run --project src/Cli/Cli.csproj -- <command>"
      },
      {
            title: "Publish a native AOT binary",
            code: "dotnet publish src/Cli/Cli.csproj -c Release -r win-x64"
      }
],
    note: 'The published binary is a self-contained native AOT executable named w365a (or w365a.exe on Windows). Copy it to a directory on your PATH.',
  },

  quickStart: [
    {
        title: "Configure your environment and default pool",
        code: "w365a config set env test\nw365a config set pool my-pool"
    },
    {
        title: "Authenticate using Azure CLI credentials",
        code: "w365a auth login"
    },
    {
        title: "Create a sandbox with ports",
        code: "w365a sandbox create --name my-sandbox --image ubuntu:22.04 --port 8080:Owner:Http"
    },
    {
        title: "List sandboxes",
        code: "w365a sandbox list"
    },
    {
        title: "Get sandbox details (includes port info)",
        code: "w365a sandbox get <sandboxId>"
    },
    {
        title: "Send a request to a sandbox port",
        code: "w365a sandbox proxy <sandboxId> --port 8080"
    },
    {
        title: "Delete a sandbox",
        code: "w365a sandbox delete <sandboxId>"
    }
] as DocStep[],

  globalOptions: [
    {
        option: "--output",
        alias: "-o",
        description: "Output format (json or table)",
        default: "table"
    },
    {
        option: "--env",
        alias: "",
        description: "Target environment (test or int)",
        default: "from config"
    },
    {
        option: "--endpoint",
        alias: "-e",
        description: "Management API endpoint URL (overrides --env)",
        default: "from config"
    },
    {
        option: "--verbose",
        alias: "-v",
        description: "Enable verbose diagnostic output",
        default: "false"
    },
    {
        option: "--version",
        alias: "",
        description: "Show version information",
        default: ""
    }
],

  commands: [
    {
        name: "sandbox create",
        description: "Create a new sandbox with the given name and image.",
        usage: "",
        options: [
            {
                option: "--name",
                alias: "-n",
                description: "Sandbox name (used for idempotency)",
                default: "required"
            },
            {
                option: "--image",
                alias: "-i",
                description: "Image identifier",
                default: "required"
            },
            {
                option: "--cpu",
                alias: "",
                description: "Processor count (Kubernetes format)",
                default: "2"
            },
            {
                option: "--memory",
                alias: "-m",
                description: "Memory size (Kubernetes format)",
                default: "4Gi"
            },
            {
                option: "--max-idle",
                alias: "",
                description: "Max idle timeout in seconds",
                default: "3600"
            },
            {
                option: "--max-lifetime",
                alias: "",
                description: "Max lifetime in seconds",
                default: "86400"
            },
            {
                option: "--min-image-revision",
                alias: "",
                description: "Minimum image revision",
                default: ""
            },
            {
                option: "--port",
                alias: "",
                description: "Port config as port:policy[:protocol] (repeatable)",
                default: ""
            },
            {
                option: "--tag",
                alias: "",
                description: "Tag as key=value (repeatable)",
                default: ""
            }
        ],
        examples: [
            {
                description: "Example",
                code: "# Basic sandbox creation\nw365a sandbox create --name my-sandbox --image ubuntu:22.04",
                output: "Sandbox 'a1b2c3d4e5f67890abcdef1234567890ab' created. (1,204 ms)\nField       Value\n----------  ------------------------------------------------\nSandbox ID  a1b2c3d4e5f67890abcdef1234567890ab\nPool        my-pool\nName        my-sandbox\nImage       ubuntu:22.04\nCPU         2\nMemory      4Gi\nState       Running\nCreated     2026-02-23 12:00:00Z\nProxy URI   https://a1b2c3d4e5f67890abcdef1234567890ab.sbproxy.example.com\nTool URI    https://tool.example.com/a1b2c3d4e5f67890abcdef1234567890ab"
            },
            {
                description: "Example",
                code: "# Custom resources and tags\nw365a sandbox create --name dev-box --image ubuntu:22.04 \\\n    --cpu 4 --memory 8Gi --max-idle 7200 \\\n    --tag team=platform --tag env=dev"
            },
            {
                description: "Example",
                code: "# With port forwarding\nw365a sandbox create --name web-app --image node:20 \\\n    --port 8080:Owner:Http --port 3000:Owner:Http"
            },
            {
                description: "Example",
                code: "# JSON output for scripting\nw365a sandbox create --name ci-runner --image ubuntu:22.04 --output json"
            }
        ]
    },
    {
        name: "sandbox list",
        description: "List sandboxes in a pool.",
        usage: "",
        examples: [
            {
                description: "Example",
                code: "# List all sandboxes in the configured pool\nw365a sandbox list",
                output: "ID                                  Name         Image         State    Created\n----------------------------------  -----------  ------------  -------  --------------------\na1b2c3d4e5f67890abcdef1234567890ab  my-sandbox   ubuntu:22.04  Running  2026-02-23 12:00:00Z\nb2c3d4e5f6a78901bcdef12345678901bc  dev-box      ubuntu:22.04  Running  2026-02-23 11:30:00Z"
            },
            {
                description: "Example",
                code: "# Filter by state and limit results\nw365a sandbox list --filter \"state eq 'Running'\" --top 5"
            },
            {
                description: "Example",
                code: "# JSON output\nw365a sandbox list --output json"
            }
        ]
    },
    {
        name: "sandbox get",
        description: "Get detailed information about a sandbox.",
        usage: "",
        examples: [
            {
                description: "Example",
                code: "w365a sandbox get a1b2c3d4e5f67890abcdef1234567890ab",
                output: "Field       Value\n----------  ------------------------------------------------\nSandbox ID  a1b2c3d4e5f67890abcdef1234567890ab\nPool        my-pool\nName        my-sandbox\nImage       ubuntu:22.04\nCPU         2\nMemory      4Gi\nState       Running\nOwner       <tenantId>/<objectId>\nCreated     2026-02-23 12:00:00Z\nProxy URI   https://a1b2c3d4e5f67890abcdef1234567890ab.sbproxy.example.com\nTool URI    https://tool.example.com/a1b2c3d4e5f67890abcdef1234567890ab\nPort 8080   Owner / Http\nPort 3000   Owner / Http2\nExpires     2026-02-24 12:00:00Z"
            },
            {
                description: "When ports are configured on the sandbox, each port is listed with its authentication policy and protocol. Use sandbox proxy to send authenticated requests to a specific port.",
                code: "# JSON output for scripting (includes full ports array)\nw365a sandbox get a1b2c3d4e5f67890abcdef1234567890ab --output json"
            }
        ]
    },
    {
        name: "sandbox delete",
        description: "Delete a sandbox. Prompts for confirmation unless --yes is passed.",
        usage: "",
        examples: [
            {
                description: "Example",
                code: "# Interactive confirmation\nw365a sandbox delete a1b2c3d4e5f67890abcdef1234567890ab",
                output: "Delete sandbox 'a1b2c3d4e5f67890abcdef1234567890ab'? [y/N] y\nSandbox 'a1b2c3d4e5f67890abcdef1234567890ab' deleted. (312 ms)"
            },
            {
                description: "Example",
                code: "# Skip confirmation (useful in scripts)\nw365a sandbox delete a1b2c3d4e5f67890abcdef1234567890ab --yes"
            }
        ]
    },
    {
        name: "sandbox proxy",
        description: "Send an authenticated HTTP request to a sandbox port via the proxy service. The command looks up the sandbox, constructs the per-port proxy URI, injects a bearer token, and prints the response.",
        usage: "",
        options: [
            {
                option: "--port",
                alias: "",
                description: "Remote sandbox port to target",
                default: "required"
            },
            {
                option: "--method",
                alias: "",
                description: "HTTP method",
                default: "GET"
            },
            {
                option: "--path",
                alias: "",
                description: "Request path and query string",
                default: "/"
            },
            {
                option: "--body",
                alias: "",
                description: "Request body (JSON string or @filepath)",
                default: ""
            }
        ],
        examples: [
            {
                description: "Example",
                code: "# Simple GET request to port 8080\nw365a sandbox proxy a1b2c3d4e5f67890abcdef1234567890ab --port 8080",
                output: "HTTP 200 OK\nContent-Type: application/json\n\n{\status\:\"healthy\"}"
            },
            {
                description: "Example",
                code: "# POST with a JSON body\nw365a sandbox proxy a1b2c3d4e5f67890abcdef1234567890ab --port 8080 \\\n    --method POST --path /api/tasks --body '{\name\:\"build\"}'"
            },
            {
                description: "Example",
                code: "# POST with body from a file\nw365a sandbox proxy a1b2c3d4e5f67890abcdef1234567890ab --port 8080 \\\n    --method POST --path /api/tasks --body @request.json"
            },
            {
                description: "Example",
                code: "# Pipe response body to jq\nw365a sandbox proxy a1b2c3d4e5f67890abcdef1234567890ab --port 8080 \\\n    --path /api/status | jq '.version'"
            }
        ]
    },
    {
        name: "auth login",
        description: "Validates Azure CLI authentication by acquiring a token for the current session.",
        usage: "",
        examples: [
            {
                description: "Example",
                code: "# Default login\nw365a auth login"
            },
            {
                description: "Example",
                code: "# Specify tenant\nw365a auth login --tenant 00000000-0000-0000-0000-000000000000"
            }
        ]
    },
    {
        name: "auth status",
        description: "Shows the current authentication state by checking Azure CLI. Displays identity claims, source, and expiry.",
        usage: ""
    },
    {
        name: "auth logout",
        description: "Tokens are not cached to disk. To sign out of Azure CLI, run az logout.",
        usage: ""
    },
    {
        name: "config set",
        description: "Set a configuration value.",
        usage: "",
        examples: [
            {
                description: "Example",
                code: "w365a config set env test\nw365a config set pool my-pool\nw365a config set cpu 4\nw365a config set memory 8Gi\nw365a config set max-idle 7200\nw365a config set tenant 00000000-0000-0000-0000-000000000000"
            }
        ]
    },
    {
        name: "config show",
        description: "Displays all configuration values with their source (config, default, or unset).",
        usage: ""
    }
] as DocCommand[],

  workflows: [
    {
        title: "Developer Sandbox Workflow",
        description: "",
        code: "# One-time setup\nw365a config set env test\nw365a config set pool dev-pool\nw365a auth login\n\n# Create a development sandbox with a port\nw365a sandbox create --name feature-xyz --image ubuntu:22.04 \\\n    --cpu 4 --memory 8Gi --port 8080:Owner:Http --tag branch=feature-xyz\n\n# Check your sandbox is running\nw365a sandbox list --filter \"sandboxName eq 'feature-xyz'\"\n\n# View sandbox details including port info\nw365a sandbox get <sandboxId>\n\n# Send a request to the sandbox\nw365a sandbox proxy <sandboxId> --port 8080 --path /api/health\n\n# When done, clean up\nw365a sandbox delete <sandboxId> --yes"
    },
    {
        title: "CI/CD Automation",
        description: "",
        code: "# Use environment variable for authentication (no interactive login)\nexport W365A_ACCESS_TOKEN=\"<bearer-token>\"\n\n# Create a sandbox and capture the ID from JSON output\nSANDBOX_JSON=$(w365a sandbox create \\\n    --name \"ci-build-$BUILD_NUMBER\" \\\n    --image ubuntu:22.04 \\\n    --endpoint https://sandbox-api.contoso.com \\\n    --pool ci-pool \\\n    --output json)\n\nSANDBOX_ID=$(echo \"$SANDBOX_JSON\" | jq -r '.sandboxId')\n\n# ... run your CI steps ...\n\n# Tear down\nw365a sandbox delete \"$SANDBOX_ID\" --yes \\\n    --endpoint https://sandbox-api.contoso.com --pool ci-pool"
    }
] as DocWorkflow[],

  auth: {
    title: 'Authentication',
    description: 'Token resolution follows a waterfall strategy:',
    methods: [
      {
            name: "W365A_ACCESS_TOKEN environment variable",
            description: "raw bearer token, ideal for automation."
      },
      {
            name: "Azure CLI",
            description: "az account get-access-token (requires az to be installed and logged in)."
      }
],
  },

  configPaths: [
    {
        platform: "Windows",
        path: "%APPDATA%\\w365a\\config.json"
    },
    {
        platform: "macOS",
        path: "~/Library/Application Support/w365a/config.json"
    },
    {
        platform: "Linux",
        path: "~/.config/w365a/config.json"
    }
],

  exitCodes: [
    {
        code: "0",
        meaning: "Success"
    },
    {
        code: "1",
        meaning: "General error"
    },
    {
        code: "2",
        meaning: "Authentication required or expired"
    },
    {
        code: "3",
        meaning: "Resource not found"
    },
    {
        code: "4",
        meaning: "Access denied"
    },
    {
        code: "5",
        meaning: "Conflict (idempotency)"
    },
    {
        code: "6",
        meaning: "No capacity available"
    }
],
};
